import { useEffect, useState } from "react";
import { X, DownloadSimple, DeviceMobile, Share } from "@phosphor-icons/react";

const DISMISSED_KEY = "yadid_pwa_dismissed_at";
const DISMISS_TTL_DAYS = 14;

/** Native beforeinstallprompt banner for Chrome/Edge/Android + a manual add-to-home-screen
 * hint for iOS Safari (which doesn't fire the event). Dismissed choice is remembered for
 * two weeks so we don't nag the partner every login. */
export default function PWAInstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    // Don't show if the user already dismissed within the TTL window.
    const dismissedAt = Number(localStorage.getItem(DISMISSED_KEY) || 0);
    const stillHidden = dismissedAt && (Date.now() - dismissedAt) < DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000;

    // Detect if already installed (running standalone) — nothing to do then.
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    if (isStandalone || stillHidden) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari never fires beforeinstallprompt. Show a manual add-to-home hint after
    // a short dwell so it doesn't slap partners the second they arrive.
    const ua = window.navigator.userAgent || "";
    const isIOS = /iP(hone|od|ad)/.test(ua) && !window.MSStream;
    const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
    if (isIOS && isSafari) {
      const t = setTimeout(() => { setIosHint(true); setVisible(true); }, 6000);
      return () => { window.removeEventListener("beforeinstallprompt", handler); clearTimeout(t); };
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice.catch(() => null);
    setDeferred(null);
    if (choice?.outcome !== "accepted") {
      // Treat "dismissed by browser" the same as manual dismissal so we don't loop.
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:w-96 z-50 border border-primary/60 bg-card shadow-2xl animate-fade-up"
      data-testid="pwa-install-banner"
      role="dialog"
      aria-label="Install YadidOS"
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 border border-primary/60 flex items-center justify-center shrink-0 bg-primary/10">
            <DeviceMobile size={18} weight="fill" className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow">Install YadidOS</div>
            <div className="text-sm mt-0.5 font-medium">
              {iosHint ? "Add YadidOS to your home screen" : "Drop YadidOS onto your home screen"}
            </div>
            <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {iosHint ? (
                <>Tap <Share size={12} weight="bold" className="inline mx-0.5 -mt-0.5" /> then <b>Add to Home Screen</b> to open YadidOS like a native app — full-screen, no browser chrome.</>
              ) : (
                <>Open the firm's command room in one tap. Full-screen, no browser chrome, offline-tolerant.</>
              )}
            </div>
          </div>
          <button
            onClick={dismiss}
            data-testid="pwa-install-dismiss"
            className="text-muted-foreground hover:text-foreground -mt-1 -mr-1 p-1"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
        {!iosHint && (
          <div className="mt-3 flex gap-2">
            <button
              onClick={install}
              data-testid="pwa-install-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 border border-primary bg-primary text-primary-foreground px-3 py-2 text-xs font-medium tracking-wide hover:bg-primary/90"
            >
              <DownloadSimple size={13} weight="bold" /> Install
            </button>
            <button
              onClick={dismiss}
              data-testid="pwa-install-later"
              className="border border-border px-3 py-2 text-xs text-muted-foreground hover:text-foreground"
            >
              Not now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
